import React, { createContext, useContext, useState, useCallback, useEffect } from "react";

const CopilotContext = createContext(null);

const WIDTH_KEY = "vidhya_copilot_width_v1";
const MIN_W = 360;
const MAX_W = 900;

export function CopilotProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("menu");
  const [ctx, setCtx] = useState(null);
  const [preset, setPreset] = useState(null);
  const [inputPreset, setInputPreset] = useState(null);
  const [actionNonce, setActionNonce] = useState(0);
  const [width, setWidthState] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem(WIDTH_KEY) || "", 10);
      if (!isNaN(v) && v >= MIN_W && v <= MAX_W) return v;
    } catch (e) {}
    return 420;
  });

  const setWidth = useCallback((w) => {
    const vw = typeof window !== "undefined" ? window.innerWidth : 1440;
    const clamped = Math.max(MIN_W, Math.min(MAX_W, Math.min(w, vw - 40), vw));
    setWidthState(clamped);
    try { localStorage.setItem(WIDTH_KEY, String(clamped)); } catch (e) {}
  }, []);

  // Guard against a stored width that overflows a smaller viewport.
  useEffect(() => {
    if (!open) return;
    const vw = window.innerWidth;
    setWidthState((w) => Math.max(MIN_W, Math.min(w, vw - 40)));
  }, [open]);

  const openCopilot = useCallback((m = "menu", context = null, options = {}) => {
    setMode(m || "menu");
    setCtx(context || null);
    setPreset(options.preset || null);
    setInputPreset(options.inputPreset || null);
    setOpen(true);
    if ((m && m !== "menu") || options.preset || options.inputPreset) setActionNonce((n) => n + 1);
  }, []);

  const consumePreset = useCallback(() => {
    const p = preset;
    setPreset(null);
    return p;
  }, [preset]);

  const consumeInputPreset = useCallback(() => {
    const p = inputPreset;
    setInputPreset(null);
    return p;
  }, [inputPreset]);

  const closeCopilot = useCallback(() => setOpen(false), []);

  const goMode = useCallback((m, context) => {
    setMode(m || "menu");
    if (context !== undefined) setCtx(context);
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && open) setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <CopilotContext.Provider value={{ open, mode, ctx, preset, inputPreset, actionNonce, width, openCopilot, closeCopilot, goMode, setWidth, consumePreset, consumeInputPreset, MIN_W, MAX_W }}>
      {children}
    </CopilotContext.Provider>
  );
}

export function useCopilot() {
  const c = useContext(CopilotContext);
  if (!c) throw new Error("useCopilot must be used within CopilotProvider");
  return c;
}
